import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cancelAppointment, listAppointments } from '../../services/portal'
import type { PortalAppointment } from '../../types'

const STATUS_LABEL: Record<string, string> = {
  pending:     'Pending',
  confirmed:   'Confirmed',
  in_progress: 'In progress',
  completed:   'Completed',
  cancelled:   'Cancelled',
}

const STATUS_STYLE: Record<string, string> = {
  pending:     'bg-amber-50 text-amber-800 border border-amber-200',
  confirmed:   'bg-nala-soft text-nala-accent border border-nala-line',
  in_progress: 'bg-nala-accent/10 text-nala-accent border border-nala-accent/20',
  completed:   'bg-nala-surface text-nala-mute border border-nala-line',
  cancelled:   'bg-red-50 text-red-700 border border-red-100',
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

export default function PortalAppointmentsPage() {
  const [scope, setScope] = useState<'upcoming' | 'past'>('upcoming')
  const qc = useQueryClient()

  const { data: appts = [], isLoading, isError } = useQuery({
    queryKey: ['portal', 'appointments', scope],
    queryFn: () => listAppointments(scope),
  })

  const cancel = useMutation({
    mutationFn: (id: number) => cancelAppointment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal', 'appointments'] })
      qc.invalidateQueries({ queryKey: ['portal', 'dashboard'] })
    },
  })

  return (
    <div className="space-y-7">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-fraunces text-4xl font-[400] tracking-tight text-nala-ink">
            Appointments
          </h1>
          <p className="text-nala-mute mt-1.5 text-[15px]">Track upcoming visits and review your history.</p>
        </div>
        <Link
          to="/book"
          className="shrink-0 inline-flex items-center gap-2 bg-nala-accent hover:bg-nala-accent/90 text-nala-accentInk font-semibold rounded-full px-5 py-2.5 text-sm transition"
        >
          + Book a visit
        </Link>
      </div>

      {/* Scope toggle */}
      <div className="inline-flex bg-white border border-nala-line rounded-full p-1 gap-1">
        {(['upcoming', 'past'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setScope(s)}
            className={`px-5 py-1.5 rounded-full text-sm font-medium capitalize transition-all ${
              scope === s
                ? 'bg-nala-accent text-nala-accentInk shadow-sm'
                : 'text-nala-mute hover:text-nala-ink'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="py-12 text-center text-sm text-nala-mute">Loading…</div>
      ) : isError ? (
        <div className="py-12 text-center text-sm text-nala-mute">
          Unable to load. Please refresh or sign in again.
        </div>
      ) : appts.length === 0 ? (
        <div className="bg-white border border-nala-line rounded-3xl p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-nala-soft flex items-center justify-center text-3xl mx-auto mb-4">📅</div>
          <h3 className="font-fraunces text-2xl font-[400] text-nala-ink">No {scope} appointments</h3>
          <p className="text-center text-sm text-nala-mute py-2">
            No {scope} appointments found.{' '}
            {scope === 'upcoming' && 'If you booked with a different email, contact us to link your account.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {appts.map((a) => (
            <AppointmentCard
              key={a.id}
              appt={a}
              onCancel={() => {
                if (confirm('Cancel this appointment?')) cancel.mutate(a.id)
              }}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function AppointmentCard({ appt, onCancel }: { appt: PortalAppointment; onCancel: () => void }) {
  const isToday = new Date(appt.scheduled_at).toDateString() === new Date().toDateString()
  const canCancel = ['pending', 'confirmed'].includes(appt.status)

  return (
    <li className="bg-white border border-nala-line rounded-2xl p-5 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap mb-1">
            <h3 className="font-fraunces text-lg font-[500] text-nala-ink leading-snug">
              {appt.service?.name ?? 'House call'}
            </h3>
            <span className={`text-[11px] px-2.5 py-0.5 rounded-full font-semibold uppercase tracking-wide ${STATUS_STYLE[appt.status] ?? 'bg-nala-surface text-nala-mute border border-nala-line'}`}>
              {STATUS_LABEL[appt.status] ?? appt.status}
            </span>
          </div>
          <p className="text-sm text-nala-mute">{fmtDate(appt.scheduled_at)}</p>
          {(appt.pet?.name || appt.veterinarian?.name) && (
            <p className="text-sm text-nala-mute mt-0.5">
              {appt.pet?.name && <>For {appt.pet.name}</>}
              {appt.pet?.name && appt.veterinarian?.name && <> · </>}
              {appt.veterinarian?.name && <>with {appt.veterinarian.name}</>}
            </p>
          )}
          {appt.address_one_line && (
            <p className="text-sm text-nala-mute mt-1">{appt.address_one_line}</p>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          {isToday && appt.track_token && (
            <Link
              to={`/portal/track/${appt.track_token}`}
              className="inline-flex items-center gap-1.5 bg-nala-accent hover:bg-nala-accent/90 text-nala-accentInk font-semibold rounded-full px-4 py-1.5 text-sm transition"
            >
              📍 Track
            </Link>
          )}
          <Link
            to={`/portal/appointments/${appt.id}`}
            className="text-sm text-nala-clay font-semibold hover:underline"
          >
            Details →
          </Link>
          {canCancel && (
            <button onClick={onCancel} className="text-xs text-nala-mute hover:text-red-600 transition">
              Cancel
            </button>
          )}
        </div>
      </div>
    </li>
  )
}
